import { Observable, Subscription } from 'rxjs';

/**
 * Returns an observable that emits the first value emitted by the first observable that emits.
 * The rest of the observables are cancelled.
 * @param inputObservables The observables to race
 *
 * @example
 * raceFirstEmit(
 *  of(1).pipe(delay(100)),
 *  of(2).pipe(delay(200)),
 *  of(3).pipe(delay(300))
 *  ).subscribe(console.log); // 1
 *
 * const obs1 = timer(500, 1000).pipe(map(() => 'A'));
 * const obs2 = timer(300, 1000).pipe(map(() => 'B'));
 * const obs3 = timer(700, 1000).pipe(map(() => 'C'));
 *
 * const raceWinner$ = raceFirstEmit(obs1, obs2, obs3)
 * raceWinner$.subscribe(console.log); // B B B
 *
 */
export function raceFirstEmit<T extends readonly unknown[]>(
  ...inputObservables: { [K in keyof T]: Observable<T[K]> }
): Observable<T[number]> {
  return createRaceObservable(inputObservables as Observable<T[number]>[]);
}

/**
 * Shared internal implementation to race observables and emit from the first one that emits.
 */
function createRaceObservable<T extends readonly unknown[]>(inputObservables: {
  [K in keyof T]: Observable<T[K]>;
}): Observable<T[number]> {
  return new Observable<T>((subscriber) => {
    let winningObservableIndex: number | null = null;
    const observableSubscriptions: (Subscription | null)[] = [];

    inputObservables.forEach((observable, currentIndex) => {
      const subscription = observable.subscribe({
        next: (emittedValue) => {
          if (winningObservableIndex === null) {
            winningObservableIndex = currentIndex;

            observableSubscriptions.forEach((activeSubscription, index) => {
              if (index !== winningObservableIndex && activeSubscription) {
                activeSubscription.unsubscribe();
                observableSubscriptions[index] = null;
              }
            });
          }

          if (currentIndex === winningObservableIndex) {
            subscriber.next(emittedValue as T);
          }
        },
        error: (error) => {
          const isWinnerOrUndecided = currentIndex === winningObservableIndex || winningObservableIndex === null;

          if (isWinnerOrUndecided) {
            subscriber.error(error);
          }
        },
        complete: () => {
          if (currentIndex === winningObservableIndex) {
            subscriber.complete();
          }
        }
      });

      observableSubscriptions.push(subscription);
    });

    return () => {
      observableSubscriptions.forEach((subscription) => subscription?.unsubscribe());
    };
  });
}
